import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '../../../config';
import { ADMIN_ROLE_KEYS, ADMIN_ROLE_LABELS_TR } from '../../../constants/AdminRoleMap';

function adminHeaders() {
  const token = localStorage.getItem('adminToken') || localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function fetchWithTimeout(url, options = {}, timeoutMs = 12000) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timeoutId);
  }
}

function normalizeStatus(status) {
  const s = String(status || '').toLowerCase().trim();
  if (s === 'testte') return 'Testte';
  if (s === 'canli') return 'Canlı';
  if (s === 'iptal') return 'İptal';
  return 'Uygulanmadı';
}

function priorityLabel(priority) {
  const p = String(priority || 'medium').toLowerCase().trim();
  if (p === 'high') return 'Yüksek';
  if (p === 'low') return 'Düşük';
  return 'Orta';
}

function dueLabel(iso) {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('tr-TR');
}

function dateInputValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

function isSmokeActionKey(actionKey) {
  return /^smoke-action-\d+$/i.test(String(actionKey || '').trim());
}

function actionTitleLabel(actionKey) {
  if (isSmokeActionKey(actionKey)) return 'Smoke Test Aksiyonu';
  return String(actionKey || '-');
}

function actionSubLabel(row = {}) {
  if (isSmokeActionKey(row.actionKey)) {
    return 'Otomatik smoke testi kaydı (canlı operasyon aksiyonu değildir).';
  }
  return `${row.drawerKey} / ${row.entityKey} • ${normalizeStatus(row.status)}`;
}

async function fetchQueue(type = 'overdue') {
  try {
    const query = new URLSearchParams();
    query.set('limit', '20');
    if (type === 'overdue') query.set('overdue', '1');
    if (type === 'unassigned') query.set('unassigned', '1');
    const res = await fetchWithTimeout(`${API_BASE_URL}/api/admin/dashboard/decision-actions?${query.toString()}`, {
      credentials: 'include',
      headers: adminHeaders()
    });
    if (!res.ok) return [];
    const json = await res.json();
    return Array.isArray(json?.items) ? json.items : [];
  } catch (_) {
    return [];
  }
}

async function fetchSuperAdminAssignees() {
  try {
    const query = new URLSearchParams();
    query.set('roleKeys', `${ADMIN_ROLE_KEYS.SUPER_ADMIN},${ADMIN_ROLE_KEYS.OPS_ADMIN}`);
    const res = await fetchWithTimeout(`${API_BASE_URL}/api/admin/dashboard/decision-actions/assignees?${query.toString()}`, {
      credentials: 'include',
      headers: adminHeaders()
    });
    if (!res.ok) return [];
    const json = await res.json();
    return Array.isArray(json?.assignees) ? json.assignees : [];
  } catch (_) {
    return [];
  }
}

export default function ActionOpsQueuePanel() {
  const [loading, setLoading] = useState(false);
  const [overdue, setOverdue] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const [forms, setForms] = useState({});
  const [assignees, setAssignees] = useState([]);
  const [helpOpen, setHelpOpen] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const [a, b, c] = await Promise.all([fetchQueue('overdue'), fetchQueue('unassigned'), fetchSuperAdminAssignees()]);
      setOverdue(a);
      setUnassigned(b);
      setAssignees(c);
      setForms((prev) => {
        const next = { ...prev };
        for (const item of [...a, ...b]) {
          const key = `${item.drawerKey}|${item.entityKey}|${item.actionKey}`;
          if (!next[key]) {
            next[key] = {
              ownerUserId: item.ownerUserId ? String(item.ownerUserId) : '',
              dueAt: dateInputValue(item.dueAt),
              priority: String(item.priority || 'medium').toLowerCase(),
              notes: String(item.notes || '')
            };
          }
        }
        return next;
      });
    } catch (_) {
      setOverdue([]);
      setUnassigned([]);
      setAssignees([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const satirKaydet = async (row) => {
    const key = `${row.drawerKey}|${row.entityKey}|${row.actionKey}`;
    const form = forms[key] || {};
    const ownerParsed = Number(form.ownerUserId || 0);

    await fetchWithTimeout(`${API_BASE_URL}/api/admin/dashboard/decision-actions/assignment`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...adminHeaders() },
      body: JSON.stringify({
        drawerKey: row.drawerKey,
        entityKey: row.entityKey,
        actionKey: row.actionKey,
        ownerUserId: ownerParsed > 0 ? ownerParsed : null,
        dueAt: form.dueAt || null,
        priority: form.priority || 'medium',
        notes: String(form.notes || '').slice(0, 500)
      })
    });

    await load();
  };

  const satirSil = async (row) => {
    const confirmDelete = window.confirm('Bu aksiyonu kuyruktan kaldırmak (İptal statüsü) istiyor musun?');
    if (!confirmDelete) return;

    const key = `${row.drawerKey}|${row.entityKey}|${row.actionKey}`;
    const form = forms[key] || {};
    const ownerParsed = Number(form.ownerUserId || 0);

    await fetchWithTimeout(`${API_BASE_URL}/api/admin/dashboard/decision-actions/status`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...adminHeaders() },
      body: JSON.stringify({
        drawerKey: row.drawerKey,
        entityKey: row.entityKey,
        actionKey: row.actionKey,
        status: 'İptal',
        resultDelta: '',
        ownerUserId: ownerParsed > 0 ? ownerParsed : null,
        dueAt: form.dueAt || null,
        priority: form.priority || 'medium',
        revenueImpact: 'medium',
        riskLevel: 'medium',
        urgency: 'backlog',
        notes: String(form.notes || '').slice(0, 500)
      })
    });

    await load();
  };

  const renderTable = (rows = [], emptyText = '') => {
    if (!rows.length) {
      return <p className="text-xs text-slate-500 dark:text-slate-400">{emptyText}</p>;
    }

    return (
      <div className="space-y-2">
        {rows.map((row) => {
          const key = `${row.drawerKey}|${row.entityKey}|${row.actionKey}`;
          const form = forms[key] || { ownerUserId: '', dueAt: '', priority: 'medium', notes: '' };
          return (
            <div key={key} className="rounded-md border border-slate-200 bg-slate-50 p-2 dark:border-slate-700 dark:bg-slate-900/40">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-xs font-semibold text-slate-700 dark:text-slate-200">{actionTitleLabel(row.actionKey)}</p>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">{actionSubLabel(row)}</p>
                </div>
                <div className="text-[11px] text-slate-500 dark:text-slate-400">Due: {dueLabel(row.dueAt)} • Öncelik: {priorityLabel(row.priority)}</div>
              </div>

              <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-6">
                <select
                  value={form.ownerUserId}
                  onChange={(e) => setForms((prev) => ({ ...prev, [key]: { ...form, ownerUserId: e.target.value } }))}
                  className="rounded border border-slate-300 bg-white px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-900"
                >
                  <option value="">{ADMIN_ROLE_LABELS_TR[ADMIN_ROLE_KEYS.SUPER_ADMIN]} / {ADMIN_ROLE_LABELS_TR[ADMIN_ROLE_KEYS.OPS_ADMIN]} seç</option>
                  {!assignees.length ? <option value="" disabled>Uygun sorumlu bulunamadı</option> : null}
                  {assignees.map((admin) => (
                    <option key={`assignee-${admin.id}`} value={String(admin.id)}>
                      {admin.name ? `${admin.name} (#${admin.id})` : `${admin.email} (#${admin.id})`}
                    </option>
                  ))}
                  {form.ownerUserId && !assignees.find((x) => String(x.id) === String(form.ownerUserId)) ? (
                    <option value={String(form.ownerUserId)}>
                      #{String(form.ownerUserId)} (listede yok)
                    </option>
                  ) : null}
                </select>
                <input
                  type="date"
                  value={form.dueAt}
                  onChange={(e) => setForms((prev) => ({ ...prev, [key]: { ...form, dueAt: e.target.value } }))}
                  className="rounded border border-slate-300 bg-white px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-900"
                />
                <select
                  value={form.priority}
                  onChange={(e) => setForms((prev) => ({ ...prev, [key]: { ...form, priority: e.target.value } }))}
                  className="rounded border border-slate-300 bg-white px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-900"
                >
                  <option value="high">Yüksek</option>
                  <option value="medium">Orta</option>
                  <option value="low">Düşük</option>
                </select>
                <input
                  type="text"
                  value={form.notes}
                  onChange={(e) => setForms((prev) => ({ ...prev, [key]: { ...form, notes: e.target.value } }))}
                  className="rounded border border-slate-300 bg-white px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-900"
                  placeholder="Not"
                />
                <button
                  type="button"
                  onClick={() => satirKaydet(row)}
                  className="rounded border border-slate-300 bg-white px-2 py-1 text-xs font-semibold text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                >
                  Kaydet
                </button>
                <button
                  type="button"
                  onClick={() => satirSil(row)}
                  className="rounded border border-red-300 bg-red-50 px-2 py-1 text-xs font-semibold text-red-700 hover:bg-red-100 dark:border-red-700 dark:bg-red-900/20 dark:text-red-300"
                >
                  Sil
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const summary = useMemo(() => ({ overdue: overdue.length, unassigned: unassigned.length }), [overdue.length, unassigned.length]);

  return (
    <section className="space-y-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-[#101922]">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Aksiyon Operasyon Kuyruğu</p>
            <button
              type="button"
              onClick={() => setHelpOpen(true)}
              className="inline-flex h-5 min-w-5 items-center justify-center rounded-full border border-amber-300 bg-amber-50 px-1 text-[11px] font-black text-amber-700 hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
              aria-label="Aksiyon Operasyon Kuyruğu nasıl çalışır"
              title="Nasıl çalışır?"
            >
              !
            </button>
          </div>
          <h3 className="text-base font-black text-slate-900 dark:text-white">Overdue ve Unassigned Yönetimi</h3>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
          <span>Gecikmiş: <strong>{summary.overdue}</strong></span>
          <span>•</span>
          <span>Atanmamış: <strong>{summary.unassigned}</strong></span>
          <button
            type="button"
            onClick={load}
            className="rounded border border-slate-300 bg-white px-2 py-1 font-semibold dark:border-slate-700 dark:bg-slate-900"
          >
            Yenile
          </button>
        </div>
      </div>

      {loading ? <p className="text-xs text-slate-500 dark:text-slate-400">Kuyruk verileri yükleniyor...</p> : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3 dark:border-amber-800 dark:bg-amber-900/10">
          <p className="mb-2 text-xs font-semibold text-amber-700 dark:text-amber-300">Gecikmiş Aksiyonlar</p>
          {renderTable(overdue, 'Gecikmiş aksiyon yok.')}
        </div>
        <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-3 dark:border-indigo-800 dark:bg-indigo-900/10">
          <p className="mb-2 text-xs font-semibold text-indigo-700 dark:text-indigo-300">Atanmamış Aksiyonlar</p>
          {renderTable(unassigned, 'Atanmamış aksiyon yok.')}
        </div>
      </div>

      {helpOpen ? (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 p-4">
          <div className="w-full max-w-xl rounded-xl border border-slate-200 bg-white p-4 shadow-xl dark:border-slate-700 dark:bg-slate-900">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Kullanım Detayı</p>
                <h4 className="text-base font-black text-slate-900 dark:text-white">Aksiyon Operasyon Kuyruğu nasıl çalışır?</h4>
              </div>
              <button
                type="button"
                onClick={() => setHelpOpen(false)}
                className="rounded border border-slate-300 bg-white px-2 py-1 text-xs font-semibold text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              >
                Kapat
              </button>
            </div>

            <div className="mt-3 space-y-2 text-sm text-slate-700 dark:text-slate-200">
              <p><strong>1.</strong> Panel iki kuyruğu gösterir: <strong>Gecikmiş</strong> ve <strong>Atanmamış</strong>.</p>
              <p><strong>2.</strong> Satır bazında <strong>Sorumlu</strong>, <strong>Termin</strong>, <strong>Öncelik</strong> ve <strong>Not</strong> girilir.</p>
              <p><strong>3.</strong> <strong>Kaydet</strong> ile atama endpointine yazılır ve kuyruk otomatik yenilenir.</p>
              <p><strong>4.</strong> Satır güncellendiğinde aksiyon bu iki kuyruğun kapsamından çıkabilir (ör. atama yapıldıysa artık atanmamış değildir).</p>
              <p><strong>5.</strong> Sağ üstteki sayaçlar o anki operasyon yükünü gösterir; <strong>Yenile</strong> butonu manuel tazeleme yapar.</p>
            </div>
            <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-2 text-xs text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
              <p className="font-semibold">Alanlar ne anlama gelir? (Örnekli)</p>
              <p className="mt-1"><strong>Sorumlu (Owner):</strong> Dropdown listesi <code>{ADMIN_ROLE_KEYS.SUPER_ADMIN}</code> + <code>{ADMIN_ROLE_KEYS.OPS_ADMIN}</code> rol havuzunu gösterir (UI: <strong>{ADMIN_ROLE_LABELS_TR[ADMIN_ROLE_KEYS.SUPER_ADMIN]}</strong> / <strong>{ADMIN_ROLE_LABELS_TR[ADMIN_ROLE_KEYS.OPS_ADMIN]}</strong>).</p>
              <p className="mt-1"><strong>Owner ID:</strong> Seçilen kişinin sistem kimliğidir (arka tarafta <code>owner_user_id</code> olarak kaydedilir). Örnek: <code>12</code>.</p>
              <p className="mt-1"><strong>Termin (Due):</strong> Bu aksiyonun en geç tamamlanması gereken tarih. Örnek: <code>2026-04-30</code>.</p>
              <p className="mt-1"><strong>Öncelik:</strong> İşin aciliyet/önem seviyesi. Örnek: kritik bug için <code>Yüksek</code>, rutin takip için <code>Orta</code>.</p>
              <p className="mt-1"><strong>Not:</strong> Kısa operasyon notu. Örnek: <code>“A/B test varyantını %20 trafikte aç, 3 gün izle”</code>.</p>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
